import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';

interface Habit {
  id: string;
  name: string;
  icon: string;
  color: string;
  target_value: number;
  unit: string;
  is_active: boolean;
  sort_order: number;
  frequency?: string;
  frequency_day?: number | null;
}

interface HabitLog {
  habit_id: string;
  value: number;
  completed: boolean;
}

interface HabitTrackerProps {
  date?: string;
}

const PRESET_ICONS = ['✓','💧','🏃','😴','🧘','📚','💪','🥗','🚶','🚴','🧗','🏊','⛰️','🙏','💊','☀️','📝','💻','🎨','🎸','🎯','💰','🧹','🛒','☕','🚭','🌱','❤️','⭐','🏆'];
const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const UNITS = ['times','glasses','minutes','hours','steps','pages','km'];
const DEFAULT_FORM = { name: '', icon: '✓', color: '#667eea', targetValue: 1, unit: 'times', frequency: 'daily', frequencyDay: 0 as number | null };

const HabitTracker: React.FC<HabitTrackerProps> = ({ date }) => {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [logs, setLogs] = useState<{ [key: string]: HabitLog }>({});
  const [streaks, setStreaks] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(true);

  // Form state: mode is null (closed), 'add', or an id (editing)
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dragId, setDragId] = useState<string | null>(null);

  const currentDate = date || new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [habitsRes, logsRes, streaksRes] = await Promise.all([
        apiService.getHabits(),
        apiService.getHabitLogs(currentDate),
        apiService.getHabitStreaks()
      ]);
      if (habitsRes.success) setHabits(habitsRes.habits || []);
      if (logsRes.success) {
        const logsMap: { [key: string]: HabitLog } = {};
        (logsRes.logs || []).forEach((log: any) => { logsMap[log.habit_id] = log; });
        setLogs(logsMap);
      }
      if (streaksRes.success) {
        const streaksMap: { [key: string]: any } = {};
        (streaksRes.streaks || []).forEach((s: any) => { streaksMap[s.id] = s; });
        setStreaks(streaksMap);
      }
    } catch (error) {
      console.error('Failed to fetch habit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshStreaks = async () => {
    const streaksRes = await apiService.getHabitStreaks();
    if (streaksRes.success) {
      const streaksMap: { [key: string]: any } = {};
      (streaksRes.streaks || []).forEach((s: any) => { streaksMap[s.id] = s; });
      setStreaks(streaksMap);
    }
  };

  const handleLogHabit = async (habitId: string, value: number) => {
    try {
      const response = await apiService.logHabit(habitId, value, currentDate);
      if (response.success) {
        setLogs({ ...logs, [habitId]: response.log });
        if (response.streakBonus) {
          const { milestone, points, habitName } = response.streakBonus;
          alert(`🎉 ${milestone}-Day Streak Bonus!\n\nYou earned ${points} bonus points for your ${habitName} streak!\n\nKeep up the great work!`);
        }
        await refreshStreaks();
      }
    } catch (error) {
      console.error('Failed to log habit:', error);
    }
  };

  // ---------- Add / Edit form ----------
  const openAdd = (frequency: string) => {
    setForm({ ...DEFAULT_FORM, frequency, frequencyDay: frequency === 'monthly' ? 1 : 0 });
    setEditingId(null);
    setFormMode('add');
  };

  const openEdit = (habit: Habit) => {
    setForm({
      name: habit.name,
      icon: habit.icon,
      color: habit.color,
      targetValue: habit.target_value,
      unit: habit.unit,
      frequency: habit.frequency || 'daily',
      frequencyDay: habit.frequency_day ?? (habit.frequency === 'monthly' ? 1 : 0)
    });
    setEditingId(habit.id);
    setFormMode('edit');
  };

  const closeForm = () => { setFormMode(null); setEditingId(null); setForm({ ...DEFAULT_FORM }); };

  const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Downscale to 96x96 to keep the stored data URL small
        const size = 96;
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, size, size);
          const dataUrl = canvas.toDataURL('image/png');
          setForm(f => ({ ...f, icon: dataUrl }));
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSaveForm = async () => {
    if (!form.name.trim()) { alert('Please enter a habit name'); return; }
    const payload: any = {
      name: form.name.trim(),
      icon: form.icon,
      color: form.color,
      targetValue: form.targetValue,
      unit: form.unit,
      frequency: form.frequency,
      frequencyDay: form.frequency === 'daily' ? null : form.frequencyDay
    };
    try {
      if (formMode === 'edit' && editingId) {
        const res = await apiService.updateHabit(editingId, payload);
        if (res.success) {
          setHabits(habits.map(h => h.id === editingId ? res.habit : h));
          closeForm();
        }
      } else {
        const res = await apiService.createHabit(payload);
        if (res.success) {
          setHabits([...habits, res.habit]);
          closeForm();
        }
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to save habit');
    }
  };

  const handleDeleteHabit = async (id: string) => {
    if (!window.confirm('Delete this habit? All tracking data will be lost.')) return;
    try {
      await apiService.deleteHabit(id);
      setHabits(habits.filter(h => h.id !== id));
    } catch (error) {
      console.error('Failed to delete habit:', error);
    }
  };

  const handleToggleActive = async (habit: Habit) => {
    try {
      await apiService.updateHabit(habit.id, { isActive: !habit.is_active });
      setHabits(habits.map(h => h.id === habit.id ? { ...h, is_active: !h.is_active } : h));
    } catch (error) {
      console.error('Failed to toggle habit:', error);
    }
  };

  // ---------- Drag and drop reorder ----------
  const persistOrder = async (ordered: Habit[]) => {
    // renumber sort_order sequentially across the whole list and persist changed ones
    const updates: Promise<any>[] = [];
    const renumbered = ordered.map((h, i) => {
      if (h.sort_order !== i) updates.push(apiService.updateHabit(h.id, { sortOrder: i }));
      return { ...h, sort_order: i };
    });
    setHabits(renumbered);
    try { await Promise.all(updates); } catch (e) { console.error('Failed to save order', e); }
  };

  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const dragged = habits.find(h => h.id === dragId);
    const target = habits.find(h => h.id === targetId);
    if (!dragged || !target) { setDragId(null); return; }
    // only reorder within the same frequency group
    const sameGroup = (isDaily(dragged) === isDaily(target));
    if (!sameGroup) { setDragId(null); return; }
    const without = habits.filter(h => h.id !== dragId);
    const targetIdx = without.findIndex(h => h.id === targetId);
    const reordered = [...without.slice(0, targetIdx), dragged, ...without.slice(targetIdx)];
    setDragId(null);
    persistOrder(reordered);
  };

  const isDaily = (h: Habit) => (h.frequency || 'daily') === 'daily';

  const renderIcon = (icon: string, size: number) => {
    if (icon && icon.startsWith('data:')) {
      return <img src={icon} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: '10px' }} />;
    }
    return <span style={{ fontSize: `${size * 0.6}px` }}>{icon}</span>;
  };

  const frequencyLabel = (h: Habit) => {
    const freq = h.frequency || 'daily';
    if (freq === 'weekly') return `Every ${WEEKDAYS[h.frequency_day ?? 0]}`;
    if (freq === 'monthly') return `Day ${h.frequency_day ?? 1} of month`;
    return 'Daily';
  };

  const activeHabits = habits.filter(h => h.is_active);
  const inactiveHabits = habits.filter(h => !h.is_active);
  const dailyHabits = activeHabits.filter(isDaily);
  const recurringHabits = activeHabits.filter(h => !isDaily(h));

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ width: 30, height: 30, border: '3px solid #e5e7eb', borderTop: '3px solid #667eea', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ---------- Card renderer ----------
  const renderCard = (habit: Habit) => {
    const log = logs[habit.id];
    const streak = streaks[habit.id];
    const progress = log ? (log.value / habit.target_value) * 100 : 0;
    const isComplete = log?.completed || false;

    if (formMode === 'edit' && editingId === habit.id) {
      return <div key={habit.id}>{renderForm()}</div>;
    }

    return (
      <div
        key={habit.id}
        draggable
        onDragStart={() => setDragId(habit.id)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => handleDrop(habit.id)}
        data-testid={`habit-card-${habit.id}`}
        style={{
          padding: '16px',
          backgroundColor: isComplete ? '#f0fdf4' : 'white',
          borderRadius: '12px',
          border: `2px solid ${isComplete ? '#86efac' : '#e5e7eb'}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          opacity: dragId === habit.id ? 0.4 : 1,
          cursor: 'grab'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: `${habit.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {renderIcon(habit.icon, 44)}
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>{habit.name}</h4>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
                Target: {habit.target_value} {habit.unit} · {frequencyLabel(habit)}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button
              onClick={() => openEdit(habit)}
              data-testid={`edit-habit-${habit.id}`}
              title="Edit habit"
              style={{ padding: '4px 8px', backgroundColor: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}
            >
              Edit
            </button>
            <button
              onClick={() => handleDeleteHabit(habit.id)}
              data-testid={`delete-habit-${habit.id}`}
              title="Delete habit"
              style={{ padding: '4px 8px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}
            >
              Del
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
            <span>{log?.value || 0} / {habit.target_value} {habit.unit}</span>
            <span>{Math.min(100, Math.round(progress))}%</span>
          </div>
          <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, progress)}%`, backgroundColor: habit.color, borderRadius: '4px', transition: 'width 0.3s ease' }} />
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button onClick={() => handleLogHabit(habit.id, (log?.value || 0) + 1)} style={{ flex: 1, padding: '8px', backgroundColor: habit.color, color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>+1</button>
          <button onClick={() => handleLogHabit(habit.id, Math.max(0, (log?.value || 0) - 1))} style={{ padding: '8px 12px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>-1</button>
          <button onClick={() => handleLogHabit(habit.id, habit.target_value)} style={{ padding: '8px 12px', backgroundColor: isComplete ? '#86efac' : '#d1fae5', color: '#065f46', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Done</button>
        </div>

        {/* Streak Info */}
        {streak && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
            <span>🔥 {streak.current_streak || 0} streak</span>
            <span>Best: {streak.longest_streak || 0}</span>
          </div>
        )}
      </div>
    );
  };

  // ---------- Add/Edit form renderer ----------
  const renderForm = () => (
    <div data-testid="habit-form" style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '2px solid #c7d2fe' }}>
      <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: 700, color: '#1f2937' }}>
        {formMode === 'edit' ? 'Edit Habit' : 'New Habit'}
      </h4>

      {/* Name + Target + Unit */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px', gap: '10px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Name</label>
          <input data-testid="habit-name-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Morning trek" style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Target</label>
          <input type="number" min={1} value={form.targetValue} onChange={(e) => setForm({ ...form, targetValue: parseInt(e.target.value) || 1 })} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Unit</label>
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}>
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>

      {/* Frequency */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Frequency</label>
          <select data-testid="habit-frequency-select" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value, frequencyDay: e.target.value === 'monthly' ? 1 : 0 })} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {form.frequency === 'weekly' && (
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Day of week</label>
            <select value={form.frequencyDay ?? 0} onChange={(e) => setForm({ ...form, frequencyDay: parseInt(e.target.value) })} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}>
              {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
        )}
        {form.frequency === 'monthly' && (
          <div>
            <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Day of month</label>
            <input type="number" min={1} max={31} value={form.frequencyDay ?? 1} onChange={(e) => setForm({ ...form, frequencyDay: Math.min(31, Math.max(1, parseInt(e.target.value) || 1)) })} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
          </div>
        )}
      </div>

      {/* Color */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Color</label>
        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: '60px', height: '34px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '6px' }} />
      </div>

      {/* Icon picker */}
      <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Icon</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${form.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {renderIcon(form.icon, 48)}
          </div>
          <button onClick={() => fileInputRef.current?.click()} data-testid="upload-icon-btn" style={{ padding: '8px 14px', backgroundColor: '#eef2ff', color: '#4f46e5', border: '1px dashed #a5b4fc', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            Upload custom icon
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleIconUpload} style={{ display: 'none' }} data-testid="icon-file-input" />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {PRESET_ICONS.map(ic => (
            <button
              key={ic}
              onClick={() => setForm({ ...form, icon: ic })}
              data-testid={`preset-icon-${ic}`}
              style={{
                width: '34px', height: '34px', borderRadius: '8px', fontSize: '1.1rem', cursor: 'pointer',
                border: form.icon === ic ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                backgroundColor: form.icon === ic ? '#eef2ff' : 'white'
              }}
            >
              {ic}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={handleSaveForm} data-testid="save-habit-btn" style={{ padding: '8px 18px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
          {formMode === 'edit' ? 'Save Changes' : 'Create Habit'}
        </button>
        <button onClick={closeForm} data-testid="cancel-habit-btn" style={{ padding: '8px 18px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );

  const sectionHeader = (title: string, subtitle: string, freq: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
      <div>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1f2937' }}>{title}</h3>
        <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '0.78rem' }}>{subtitle}</p>
      </div>
      <button
        onClick={() => openAdd(freq)}
        data-testid={`add-${freq === 'daily' ? 'daily' : 'recurring'}-habit-btn`}
        style={{ padding: '8px 14px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
      >
        + Add
      </button>
    </div>
  );

  return (
    <div style={{ padding: '20px' }} data-testid="habit-tracker">
      {/* Top header */}
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#1f2937' }}>Habits & Routines</h2>
        <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
          {new Date(currentDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} · Drag cards to reorder
        </p>
      </div>

      {/* Add form (top) shown when adding */}
      {formMode === 'add' && (
        <div style={{ marginBottom: '20px' }}>{renderForm()}</div>
      )}

      {/* DAILY SECTION */}
      <div style={{ marginBottom: '28px' }}>
        {sectionHeader('Daily Habits', 'Track these every day to build streaks', 'daily')}
        {dailyHabits.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {dailyHabits.map(renderCard)}
          </div>
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', backgroundColor: '#f9fafb', borderRadius: '12px', fontSize: '0.85rem' }}>
            No daily habits yet. Click &quot;+ Add&quot; to create one.
          </div>
        )}
      </div>

      {/* RECURRING SECTION */}
      <div style={{ marginBottom: '28px' }}>
        {sectionHeader('Weekly & Monthly Habits', 'For things like Sunday treks or monthly social service', 'weekly')}
        {recurringHabits.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {recurringHabits.map(renderCard)}
          </div>
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', backgroundColor: '#f9fafb', borderRadius: '12px', fontSize: '0.85rem' }}>
            No weekly or monthly habits yet. Click &quot;+ Add&quot; to create one.
          </div>
        )}
      </div>

      {/* Inactive / available habits */}
      {inactiveHabits.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', fontWeight: 600, color: '#6b7280' }}>Available Habits (tap to enable)</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {inactiveHabits.map(habit => (
              <button key={habit.id} onClick={() => handleToggleActive(habit)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '20px', cursor: 'pointer', fontSize: '0.875rem' }}>
                {renderIcon(habit.icon, 20)}
                <span>{habit.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HabitTracker;
